import React from "react";
import { motion } from "framer-motion";
import { Sparkle, Envelope, CalendarBlank, FileText, ShieldCheck } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Redirect must be the app root so Emergent Auth returns cleanly on all browsers/mobile.
// AppRouter in App.js catches `#session_id=...` on the root and renders AuthCallback.
function getAuthUrl() {
  const redirectUrl = window.location.origin + "/";
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const features = [
  { icon: Envelope, title: "Unified Inbox", desc: "Gmail + Messenger in one place, with AI-drafted replies." },
  { icon: CalendarBlank, title: "Smart Scheduling", desc: "Calendar-aware slot suggestions. No double bookings." },
  { icon: FileText, title: "AI-Drafted Quotes", desc: "Priced from your catalog. Never invented." },
  { icon: ShieldCheck, title: "Human-in-the-loop", desc: "Nothing goes out until you approve." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-neutral-950 text-neutral-950 dark:text-neutral-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-40 backdrop-blur-xl bg-white/70 dark:bg-neutral-900/70">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0F52BA] flex items-center justify-center">
              <Sparkle weight="fill" className="text-white" size={18} />
            </div>
            <span className="font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>AI Office Manager</span>
          </div>
          <a
            data-testid="landing-signin-btn"
            href={getAuthUrl()}
            className="text-sm px-4 py-2 rounded-md bg-neutral-950 text-white dark:bg-neutral-100 dark:text-neutral-950 hover:bg-neutral-800 transition-colors"
          >
            Sign in with Google
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-20 pb-24">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-6">Virtual office assistant · Human-approved</div>
            <h1
              className="text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tight leading-[0.95]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              The quiet colleague<br />
              that <span className="text-[#0F52BA]">handles the</span><br />
              admin backlog.
            </h1>
            <p className="mt-8 text-lg text-neutral-600 dark:text-neutral-400 dark:text-neutral-500 max-w-xl leading-relaxed">
              Receives customer enquiries from Gmail and Messenger, analyses intent with GPT-5.2,
              drafts a reply, checks your calendar, and prepares a quote — then waits for your green light.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <a
                data-testid="landing-cta-signin"
                href={getAuthUrl()}
                className="px-6 py-3 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094] transition-colors font-medium inline-block"
              >
                Start with Google
              </a>
              <a
                href="#how"
                className="px-6 py-3 rounded-md border border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              >
                How it works
              </a>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 bg-white dark:bg-neutral-900 shadow-sm">
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-3">Inbox · Gmail</div>
              <div className="text-sm text-neutral-900 dark:text-neutral-100 mb-4"><b>Jamie Rivera</b> — Quote for kitchen deep clean?</div>
              <div className="border-l-2 border-[#0F52BA] pl-3 text-sm text-neutral-700 dark:text-neutral-300 mb-4">
                Hi — we just moved in and I&rsquo;d love a quote for a kitchen deep clean next Tuesday morning…
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-2">AI draft · 92% confidence</div>
              <div className="text-sm text-neutral-900 dark:text-neutral-100 rounded-md bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 p-3">
                Hi Jamie — happy to help. For a kitchen deep clean on a 2-bed apartment, we quote $320 flat.
                Tuesday 9:00–10:00 works — shall I hold it?
              </div>
              <div className="mt-4 flex gap-2">
                <div className="text-xs px-2 py-1 rounded bg-neutral-950 text-white dark:bg-neutral-100 dark:text-neutral-950">Approve & send</div>
                <div className="text-xs px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700">Edit</div>
              </div>
            </div>
          </motion.div>
        </div>

        <div id="how" className="mt-32 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 * i }}
              className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 bg-white dark:bg-neutral-900 hover:shadow-md transition-shadow"
            >
              <f.icon size={22} className="text-[#0F52BA]" weight="duotone" />
              <div className="mt-4 font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{f.title}</div>
              <div className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 dark:text-neutral-500 leading-relaxed">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </main>
      <footer className="border-t border-neutral-200 dark:border-neutral-800 py-8 text-xs text-neutral-500 dark:text-neutral-500 text-center">
        © 2026 AI Office Manager · Built for service businesses
      </footer>
    </div>
  );
}
